import {
  Flex,
  HStack,
  Icon,
  Switch,
  Text,
  Textarea,
  VStack,
} from '@chakra-ui/react'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import IconClock from '~/assets/svgs/icon-clock.svg?react'
import { DropdownSelect } from '~/components/shared/Dropdown/DropdownSelect'
import { Dropzone } from '~/components/shared/Dropzone'
import { TagInput } from '~/components/shared/TagInput'
import { ContactSearchInput } from '~/components/specialized/ContactSearchInput/ContactSearchInput'
import { FaxAttachmentConfigMultifile } from '~/constants/fileAttachmentConfig'
import { useNewFaxFormStyle } from '~/pages/Home/Modals/NewFaxModal/componentStyles/NewFaxForm.style'
import { useGetAllPhoneNumbers } from '~/queries/phoneNumberQueries'
import { Spacing } from '~/types/ThemeTypes'
import { isValidEmail } from '~/utils/common'
import { formatPhoneNumber } from '~/utils/formatter'

import { ContactSourceType, PhoneNumber } from '@mangovoice/types'

import { type NewFax, useNewFaxViewModel } from './viewModels'

interface NewFaxMenuProps {
  selectedFrom: PhoneNumber | null
  to: string
  isToInvalid: boolean
  sendEmailConfirmation: boolean
  emailConfirmations: string[]
  includeCoverPage: boolean
  coverPageFile: File | null
  coverPageNote: string
  setCoverPageNote: (note: string) => void
  setIncludeCoverPage: (state: boolean) => void
  onToChanged: (to: string) => void
  onFromSelected: (from: PhoneNumber | null) => void
  onFileSelectionChanged: (files: File[]) => void
  onEmailConfirmationToggled: (enabled: boolean) => void
  onEmailConfirmationsChanged: (emails: string[]) => void
}

const maxEmailConfirmations = 5
export const NewFaxForm = ({
  selectedFrom,
  to,
  isToInvalid,
  sendEmailConfirmation,
  emailConfirmations,
  includeCoverPage,
  coverPageFile,
  coverPageNote,
  setCoverPageNote,
  setIncludeCoverPage,
  onToChanged,
  onFromSelected,
  onFileSelectionChanged,
  onEmailConfirmationToggled,
  onEmailConfirmationsChanged,
}: NewFaxMenuProps) => {
  const styles = useNewFaxFormStyle()
  const {
    state: { lastUsedFaxNumber },
  } = useNewFaxViewModel()
  const { results: allPhoneNumbers } = useGetAllPhoneNumbers()
  const { t } = useTranslation()

  const fromList = allPhoneNumbers
    .filter((phoneNumber) => phoneNumber.id !== lastUsedFaxNumber?.id)
    .map((phoneNumber) => phoneNumber as NewFax.FaxListPhoneNumber)
  if (fromList && lastUsedFaxNumber) {
    fromList.unshift({ ...lastUsedFaxNumber, isRecent: true })
  }

  const handleDropAccepted = (files: File[]) => {
    if (!files.length) {
      return
    }

    onFileSelectionChanged(files)
  }

  const handleDropzoneCleared = () => {
    onFileSelectionChanged([])
  }

  const handleEmailConfirmationToggled = () => {
    onEmailConfirmationToggled(!sendEmailConfirmation)
  }

  const handleTagsUpdated = (tags: string[]) => {
    onEmailConfirmationsChanged(tags)
  }

  const numbersJsx = useMemo(() => {
    return fromList.map((phoneNumber) => {
      const formattedPhoneNumber = formatPhoneNumber(phoneNumber.number)
      return {
        value: phoneNumber.id,
        content: (
          <HStack key={phoneNumber.id} p="0.5rem">
            {phoneNumber.isRecent && <Icon as={IconClock} />}
            <Text sx={styles.phoneNameText}>{phoneNumber.name}</Text>
            <Text
              sx={styles.phoneNumberText}
            >{` - ${formattedPhoneNumber}`}</Text>
          </HStack>
        ),
      }
    })
  }, [fromList])

  const handleSelectedInternal = (id: number, index: number) => {
    const selectedFrom = fromList[index]
    onFromSelected(selectedFrom)
  }

  const handleToChangedInternal = (newToValue: string) => {
    onToChanged(newToValue)
  }

  const getSelectedFromPhoneNumberIndex = (from: PhoneNumber | null) => {
    if (!from) {
      return
    }

    const index = fromList.findIndex((phoneNumber) => {
      return phoneNumber.id === from.id
    })

    return index
  }

  const selectedPhoneNumberIndex = useMemo(() => {
    return getSelectedFromPhoneNumberIndex(selectedFrom)
  }, [selectedFrom, fromList])

  return (
    <VStack align="stretch" spacing={Spacing._2}>
      <DropdownSelect
        placeholder={t('newFaxForm.dropdownPlaceholder')}
        items={numbersJsx}
        selectedIndex={selectedPhoneNumberIndex}
        onSelect={handleSelectedInternal}
      />
      <ContactSearchInput
        scope="fax"
        isInvalid={isToInvalid}
        value={to}
        onValueChanged={handleToChangedInternal}
        contactSourceTypeFilter={[ContactSourceType.Extensions]}
      />
      <VStack align="stretch" spacing={Spacing._4}>
        <HStack justify="flex-start">
          <Switch
            isChecked={includeCoverPage}
            onChange={() => setIncludeCoverPage(!includeCoverPage)}
          />
          <Text>{t('newFaxForm.switchText.includeCoverPage')}</Text>
        </HStack>
        {includeCoverPage && (
          <Textarea
            placeholder={t('newFaxForm.textAreaPlaceholder')}
            value={coverPageNote}
            onChange={(e) => setCoverPageNote(e.target.value)}
          />
        )}
      </VStack>
      <Dropzone
        coverPage={coverPageFile}
        handleCoverPageDelete={() => setIncludeCoverPage(false)}
        acceptedFileTypes={FaxAttachmentConfigMultifile.AcceptedFileTypes}
        allowMultiple={FaxAttachmentConfigMultifile.MultipleFiles}
        dropzoneProps={{
          onDropAccepted: handleDropAccepted,
        }}
        maxFileSize={FaxAttachmentConfigMultifile.MaximumFileSize}
        maxFileSizePlaceholder={t('newFaxForm.dropzonePlaceholder')}
        onClear={handleDropzoneCleared}
        onFileChange={handleDropAccepted}
      />
      <Flex align="stretch" height={Spacing._2} />
      <VStack align="stretch" spacing={Spacing._4}>
        <HStack justify="flex-start">
          <Switch
            isChecked={sendEmailConfirmation}
            onChange={handleEmailConfirmationToggled}
          />
          <Text>{t('newFaxForm.switchText.sendEmailConfirmation')}</Text>
        </HStack>
        {sendEmailConfirmation && (
          <TagInput
            placeholder={t('newFaxForm.tagInputPlaceholder')}
            guideText={t('newFaxForm.tagInputGuideText', {
              maxEmailConfirmations,
            })}
            valueValidator={isValidEmail}
            valueType="email"
            initialTags={emailConfirmations}
            onTagsUpdated={handleTagsUpdated}
          />
        )}
      </VStack>
    </VStack>
  )
}
